/**
 * React Query hooks for Metabase URL fetching.
 * React Query hooks for the corresponding entity.
 */

import { Capacitor } from "@capacitor/core";
import { useQuery } from "@tanstack/react-query";
import { queryKeys } from "common/queryKeys";

export type MetabaseUrl = {
  url: string;
  visualizationId: string;
};

export enum METABASE_TYPES {
  QUESTION = "QUESTION",
  DASHBOARD = "DASHBOARD",
}

class MetabaseUrlApi {
  baseUrl: string;

  constructor() {
    this.baseUrl = `${import.meta.env.VITE_API_ENDPOINT}/v1/metabaseUrl`;
  }

  async getOne(
    visualizationId: string,
    params: { [name: string]: string }
  ): Promise<MetabaseUrl> {
    let paramsString = "";
    for (const key in params) {
      paramsString = paramsString + `&${key}=${params[key]}`;
    }
    const finalUrl = `${this.baseUrl}?visualizationId=${visualizationId}${paramsString}`;
    const res = await fetch(finalUrl, {
      mode: "cors",
      method: "GET",
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt") || ""}`,
        "Content-Type": "application/json",
        "Access-Control-Allow-Origin": "*",
        "Access-Control-Allow-Headers":
          "authorization, x-client-info, apikey, content-type",
      },
    });
    let url = (await res.json()).url;
    if (Capacitor.getPlatform() === "android") {
      url = url.replace("localhost:54321", "10.0.2.2:54321");
    }
    return {
      url,
      visualizationId: visualizationId,
    } as MetabaseUrl;
  }
}

const metabaseUrlApi = new MetabaseUrlApi();

/**
 * Fetch a Metabase URL for a specific visualization.
 * The query key includes the visualizationId and params to cache different URLs separately.
 */
export function useMetabaseUrl(
  visualizationId: string | null | undefined,
  params: { [name: string]: string } = {},
  enabled = true
) {
  // Create a stable params key for caching
  const paramsKey = Object.entries(params)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([k, v]) => `${k}=${v}`)
    .join("&");

  return useQuery({
    queryKey: [
      ...queryKeys.dashboards.metabaseUrl(visualizationId || ""),
      paramsKey,
    ],
    queryFn: async () => {
      if (!visualizationId) {
        throw new Error("visualizationId is required");
      }
      return metabaseUrlApi.getOne(visualizationId, params);
    },
    enabled: enabled && !!visualizationId,
    staleTime: 5 * 60 * 1000, // 5 minutes - Metabase URLs don't change often
  });
}

export { metabaseUrlApi };
